'use client';

import { PropsWithChildren, useCallback } from 'react';

import { ThemeProvider as NextThemesProvider, useTheme as useNextTheme } from 'next-themes';
import { useEvent, useUpdateEffect } from 'react-use';

import { Theme } from '@/common/enums';
import { useScopedTheme } from '@/components/providers/scoped-theme-provider';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { useSyncEvent } from '@/lib/sync/hooks/use-sync-event';

interface ThemeProviderProps extends PropsWithChildren {
  defaultTheme?: Theme;
}

function ThemeSyncLogic() {
  const { theme, setTheme } = useNextTheme();
  const { emit } = useCrossTabSync();

  useUpdateEffect(() => {
    if (theme) {
      emit(SYNC_EVENTS.THEME, theme as Theme);
    }
  }, [theme, emit]);

  const handleThemeChange = useCallback(
    (newTheme: Theme) => {
      if (newTheme !== theme) {
        setTheme(newTheme);
      }
    },
    [theme, setTheme],
  );

  useSyncEvent(SYNC_EVENTS.THEME, handleThemeChange);

  // Handle postMessage for iframe/widget communication
  useEvent('message', (event: MessageEvent) => {
    if (event.data.type === 'setTheme') {
      setTheme(event.data.theme);
    }
  });

  return null;
}

export function ThemeProvider({ children, defaultTheme = Theme.Light }: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={defaultTheme}
      enableSystem
      disableTransitionOnChange
    >
      <ThemeSyncLogic />
      {children}
    </NextThemesProvider>
  );
}

/** Shape returned by the unified `useTheme` hook. */
interface UseThemeReturn {
  theme?: string;
  resolvedTheme?: string;
  systemTheme?: 'dark' | 'light';
  setTheme: (theme: string) => void;
  themes: string[];
}

/**
 * Unified theme hook. Works in both contexts:
 * - Inside `ScopedThemeProvider` (StandaloneWrapper / npm consumers): uses scoped context
 * - Inside `next-themes` `ThemeProvider` (main app): uses next-themes
 *
 * Returns `{ theme, setTheme, resolvedTheme, systemTheme, themes }`.
 */
export function useTheme(): UseThemeReturn {
  const scoped = useScopedTheme();
  const nextThemes = useNextTheme();

  // Prefer scoped context when available (StandaloneWrapper / npm)
  if (scoped) {
    return scoped;
  }

  return nextThemes;
}
